import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Switch,
  VStack,
  HStack,
  Heading,
  Divider,
  Text,
  useToast,
} from "@chakra-ui/react";
import supabase from "../lib/supabaseClient";
import BilingualField from "./BilingualField";

// Client-only stable key for React list rendering; stripped before save.
const STAT_KEY = "__key";
let statKeyCounter = 0;
function makeStatKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  statKeyCounter += 1;
  return `stat-${statKeyCounter}`;
}

const EMPTY_STAT = {
  icon: "",
  value: "",
  label_es: "",
  label_en: "",
  sublabel_es: "",
  sublabel_en: "",
};

// Strip client-only keys (STAT_KEY) from stats before persisting.
function cleanStats(stats) {
  return (stats || []).map(({ [STAT_KEY]: _drop, ...rest }) => rest);
}

const EMPTY_PROFILE = {
  id: null,
  name: "",
  role_es: "",
  role_en: "",
  about_es: "",
  about_en: "",
  photo_url: "",
  available: false,
  available_text_es: "",
  available_text_en: "",
  github: "",
  linkedin: "",
  email: "",
  whatsapp: "",
  website: "",
  phone: "",
  stats: [],
};

export default function ProfileForm() {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    const loadProfile = async () => {
      const { data, error } = await supabase
        .from("profile")
        .select("*")
        .limit(1)
        .maybeSingle();

      if (error) {
        toast({ title: "Error cargando perfil", status: "error", duration: 3000 });
      } else if (data) {
        const stats = (data.stats || []).map((s) => ({
          ...s,
          [STAT_KEY]: makeStatKey(),
        }));
        setProfile({ ...EMPTY_PROFILE, ...data, stats });
      }
      setLoading(false);
    };
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, value) => setProfile((prev) => ({ ...prev, [key]: value }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    // Use a sanitized key (extension only) — original filenames with spaces or
    // accents produce an "Invalid key" 400 from Supabase Storage.
    const ext = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "");
    const path = `profile/photo-${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from("media")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (error) {
      toast({ title: "Error subiendo foto", description: error.message, status: "error", duration: 3000 });
    } else {
      const { data } = supabase.storage.from("media").getPublicUrl(path);
      set("photo_url", data.publicUrl);
      toast({ title: "Foto subida", status: "success", duration: 2000 });
    }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    let error;
    // Drop the client-only id placeholder and strip stats' client-only keys.
    const { id, ...rest } = profile;
    const payload = { ...rest, stats: cleanStats(profile.stats) };

    if (id) {
      // Update existing row by id
      const { error: updateError } = await supabase
        .from("profile")
        .update(payload)
        .eq("id", id);
      error = updateError;
    } else {
      // Insert new row (no id -> let DB generate it). Use maybeSingle, never single.
      const { data, error: insertError } = await supabase
        .from("profile")
        .insert(payload)
        .select()
        .maybeSingle();
      error = insertError;
      if (!insertError && data) {
        // Store generated id so the next save is an UPDATE, not another INSERT.
        setProfile((prev) => ({ ...prev, id: data.id }));
      }
    }

    if (error) {
      toast({ title: "Error guardando", description: error.message, status: "error", duration: 4000 });
    } else {
      toast({ title: "Guardado correctamente", status: "success", duration: 2000 });
    }
    setSaving(false);
  };

  const updateStat = (index, key, value) => {
    const newStats = [...profile.stats];
    newStats[index] = { ...newStats[index], [key]: value };
    set("stats", newStats);
  };

  const addStat = () =>
    set("stats", [...profile.stats, { ...EMPTY_STAT, [STAT_KEY]: makeStatKey() }]);

  const removeStat = (index) => {
    const newStats = profile.stats.filter((_, i) => i !== index);
    set("stats", newStats);
  };

  if (loading) return <Box p={4}>Cargando...</Box>;

  return (
    <VStack spacing={6} align="stretch" maxW="900px">
      <Heading size="md">Perfil</Heading>
      <Text fontSize="sm" color="text.muted">
        <Box as="span" color="red.400">*</Box> Campos obligatorios
      </Text>

      {/* Name */}
      <FormControl isRequired>
        <FormLabel>Nombre</FormLabel>
        <Input value={profile.name} onChange={(e) => set("name", e.target.value)} />
      </FormControl>

      {/* Role bilingual */}
      <BilingualField
        label="Rol / Título"
        required
        valueEs={profile.role_es}
        valueEn={profile.role_en}
        onChangeEs={(v) => set("role_es", v)}
        onChangeEn={(v) => set("role_en", v)}
      />

      {/* About bilingual */}
      <BilingualField
        label="Sobre mí"
        valueEs={profile.about_es}
        valueEn={profile.about_en}
        onChangeEs={(v) => set("about_es", v)}
        onChangeEn={(v) => set("about_en", v)}
        type="textarea"
      />

      {/* Available */}
      <FormControl>
        <HStack>
          <Switch
            isChecked={profile.available}
            onChange={(e) => set("available", e.target.checked)}
          />
          <FormLabel mb={0}>Disponible</FormLabel>
        </HStack>
      </FormControl>

      <BilingualField
        label="Texto disponibilidad"
        valueEs={profile.available_text_es}
        valueEn={profile.available_text_en}
        onChangeEs={(v) => set("available_text_es", v)}
        onChangeEn={(v) => set("available_text_en", v)}
      />

      <Divider />

      {/* Contact / Links */}
      <Heading size="sm">Contacto y enlaces</Heading>

      <FormControl isRequired>
        <FormLabel>Email</FormLabel>
        <Input type="email" value={profile.email} onChange={(e) => set("email", e.target.value)} />
      </FormControl>

      <FormControl>
        <FormLabel>WhatsApp</FormLabel>
        <Input value={profile.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
      </FormControl>

      <FormControl>
        <FormLabel>Teléfono</FormLabel>
        <Input value={profile.phone} onChange={(e) => set("phone", e.target.value)} />
      </FormControl>

      <FormControl>
        <FormLabel>GitHub</FormLabel>
        <Input value={profile.github} onChange={(e) => set("github", e.target.value)} />
      </FormControl>

      <FormControl>
        <FormLabel>LinkedIn</FormLabel>
        <Input value={profile.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
      </FormControl>

      <FormControl>
        <FormLabel>Website</FormLabel>
        <Input value={profile.website} onChange={(e) => set("website", e.target.value)} />
      </FormControl>

      <Divider />

      {/* Photo */}
      <Heading size="sm">Foto</Heading>
      <FormControl>
        <FormLabel>URL foto</FormLabel>
        <Input value={profile.photo_url} onChange={(e) => set("photo_url", e.target.value)} />
      </FormControl>
      <Box>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handlePhotoUpload}
        />
        <Button
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          isLoading={uploading}
          loadingText="Subiendo..."
        >
          Subir foto
        </Button>
      </Box>

      <Divider />

      {/* Stats */}
      <HStack justify="space-between">
        <Heading size="sm">Estadísticas</Heading>
        <Button size="sm" onClick={addStat}>+ Añadir stat</Button>
      </HStack>

      {profile.stats.map((stat, i) => (
        <Box key={stat[STAT_KEY]} p={3} border="1px solid" borderColor="border.card" borderRadius="md">
          <HStack mb={2} justify="space-between">
            <Text fontWeight="bold" fontSize="sm">Stat {i + 1}</Text>
            <Button size="xs" colorScheme="red" variant="outline" onClick={() => removeStat(i)}>
              Eliminar
            </Button>
          </HStack>
          <VStack align="stretch" spacing={2}>
            <HStack>
              <FormControl>
                <FormLabel fontSize="xs">Icono</FormLabel>
                <Input size="sm" value={stat.icon} onChange={(e) => updateStat(i, "icon", e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">Valor</FormLabel>
                <Input size="sm" value={stat.value} onChange={(e) => updateStat(i, "value", e.target.value)} />
              </FormControl>
            </HStack>
            <BilingualField
              label="Etiqueta"
              valueEs={stat.label_es}
              valueEn={stat.label_en}
              onChangeEs={(v) => updateStat(i, "label_es", v)}
              onChangeEn={(v) => updateStat(i, "label_en", v)}
            />
            <BilingualField
              label="Sub-etiqueta"
              valueEs={stat.sublabel_es}
              valueEn={stat.sublabel_en}
              onChangeEs={(v) => updateStat(i, "sublabel_es", v)}
              onChangeEn={(v) => updateStat(i, "sublabel_en", v)}
            />
          </VStack>
        </Box>
      ))}

      <Button
        colorScheme="blue"
        onClick={handleSave}
        isLoading={saving}
        loadingText="Guardando..."
        alignSelf="flex-start"
        isDisabled={
          !(
            profile.name?.trim() &&
            profile.role_es?.trim() &&
            profile.role_en?.trim() &&
            profile.email?.trim()
          )
        }
      >
        Guardar
      </Button>
    </VStack>
  );
}
